import { SportSettings, SportSetting } from '@/types/settings';
import { BetTypeSettings, BetTypeSetting, LayoutType } from '@/types/bet-type-settings';
import { siteSettings } from '@/settings/site-settings';
import { 
	galaxyForbiddenSports, 
	marsForbiddenSports, 
	sportSettings,
	numberGameIds,
	virtualSportIds,
	betbyVirtualSports,
	dummySettings,
} from '@/settings/sports-settings';
import { betTypeSettings } from '@/settings/bet-type-settings';
import { SportCategorySettings, CategorySettings, CategoryNameCriteria, CategoryBetTypeSettingCriteria, Category } from '@/types/category-setting';
import { sportCategorySettings, categorySettings  } from '@/settings/category-settings';
import { Market } from '@nf/types/odds';
import { ViewEnum } from '@/types';
import { galaxyMarketMenuPriorityMapping, galaxyMenuMarketMapping, marsMarketMenuPriorityMapping, marsMenuMarketMapping, marsParlayMarketMenuPriorityMapping } from '@/settings/menu-type-settings';
import { viewLayoutSettings } from '@/settings/layout-settings/view-layout-settings';
import { viewChoiceSettings } from '@/settings/choice-settings/view-choice-settings';
import { newbieSportSettings } from '@/settings/newbie-sport-settings';
import { betTypeTutorialSettings } from '@/settings/bet-type-tutorial-settings';
import { specialSportSettings } from '@/settings/special-sport-setting';
import { sportService } from '@/services/sport.service';

export const getSiteProtectedPaths = () => siteSettings.protectedPaths;

export const getSiteIgnoredCheckPaths = () => siteSettings.ignoredCheckPaths;

export const getSportSetting = (sport: number): SportSetting => {
	return sportSettings[sport as keyof SportSettings] ?? sportSettings[0];
};

export const getNewbieSportSettings = (sport: number): SportSetting => newbieSportSettings[sport] ?? newbieSportSettings[0];

export const getBetTypeSetting = (betType: number): BetTypeSetting => {
	return betTypeSettings[betType as keyof BetTypeSettings];
};

export const getCustomizeBetChoice = (betType: number, view: ViewEnum = ViewEnum.galaxy) => {
	return viewChoiceSettings[view]?.[betType];
}

export const getSportCategorySetting = (sportType: number): Array<Category> => {
	return sportCategorySettings[sportType as keyof SportCategorySettings] ?? sportCategorySettings[0];
}

export const getCategoryName = ({ sportType, category }: CategoryNameCriteria): string => {
	const categorySetting = categorySettings[category as keyof CategorySettings];
	return categorySetting.title[sportType] ?? categorySetting.title[0];
}

export const isSpecialLeague = (category: Category): boolean => {
	const subMatchCategories: Category[] = ['Main', 'Half', 'Full', 'ExtraTime', 'Penalty', 'Corners', 'Intervals', 'Specials', 'Players', 'Innings', 'Hits', 'Match'];
	
	return subMatchCategories.includes(category);
}

export const getSpecialLeagueCategoryName = (sportId: number, cmt: number, betTypes: Market[] = []): Category => {
	let categoryName: Category = [1, 6, 9, 50].includes(sportId) ? 'Main' : 'Full';

	switch (cmt) {
		case 201:
			categoryName = 'ExtraTime';
			break;
		case 202:
		case 203:
			categoryName = 'Penalty';
			break;
		case 204:
		case 205:
		case 209:
			categoryName = 'Corners';
			break;
		case 206:
			categoryName = 'Intervals';
			break;
		case 207:
			categoryName = 'Specials';
			break;
		case 208:
			categoryName = 'Players';
			break;
		case 210:
			categoryName = 'Full';
			break;
		case 211:
			categoryName = 'Half';
			break;
	}

	if (sportId == 43) {
		const cat = betTypes.filter(betType => [1, 3, 7, 8, 20].includes(betType.BetTypeId))[0]?.Cat;
		if (cat > 10) {
			return `Map${cat - 10}` as keyof CategorySettings;
		}
	}

	return categoryName;
}

export const getCategoryBetTypeSettings = ({
	sportType, 
	category,
	isLive,
	gameType,
	parentMatchId,
	isFilterBBPlus
}: CategoryBetTypeSettingCriteria & { parentMatchId: number, isFilterBBPlus: boolean }): Array<number> | undefined => {

	const categorySetting = categorySettings[category as keyof CategorySettings] ?? categorySettings['Main'];

	const key = getCategoryBetTypeSettingKey({ sportType, category, isLive, gameType });

	const betTypes = categorySetting.betTypeSettings[key] ?? categorySetting.betTypeSettings[0];

	const shouldPrependBBPlusCornerBooking =
		isFilterBBPlus && category === 'Corners' && parentMatchId > 0 && sportType === 1; // 只有足球才需要 prepend BB+ corner booking

	return shouldPrependBBPlusCornerBooking
		? [...BB_PLUS_CORNER_BOOKING_BET_TYPES, ...betTypes]
		: betTypes;
}

export const getForbiddenSport = (view: ViewEnum) => {
	return view === ViewEnum.galaxy ? galaxyForbiddenSports : marsForbiddenSports;
}

export const getMarketPriority = (view: ViewEnum, isParlay?: boolean) => {
	if (view === ViewEnum.galaxy) {
		return galaxyMarketMenuPriorityMapping;
	}
	
	return isParlay ? marsParlayMarketMenuPriorityMapping : marsMarketMenuPriorityMapping;
}

export const getMenuMarket = (view: ViewEnum) => {	
	return view === ViewEnum.galaxy ? galaxyMenuMarketMapping : marsMenuMarketMapping;
}

export const isDummySportCategory = (sportId: number) => dummySettings.findIndex(setting => setting.categoryId === sportId) !== -1

export const isNumberGames = (sportId: number) => {
	return [161, 164].includes(sportId);
}

/** Galaxy：不掛 `ThirdPartyTopMenu`（`page-top-bar__title`）的 sport，避免與 seal-components 內建標題重疊 */
export const GALAXY_THIRD_PARTY_TOP_MENU_EXCLUDED_SPORTS: ReadonlyArray<number> = [112];

export const isGalaxyThirdPartyTopMenuExcludedSport = (sportId: number): boolean =>
	GALAXY_THIRD_PARTY_TOP_MENU_EXCLUDED_SPORTS.includes(sportId);
export const isEndingGames = (sportId: number, betTypeId: number) => {
	return sportId === 2 && betTypeId >= 647 && betTypeId <= 650;
}
export const is1x2CustomizationSettingSport = (sportId: number) => {
	return [1, 997, 9994, 9995].includes(sportId);
}

const getCategoryBetTypeSettingKey = ({
	sportType,
	isLive,
	gameType
}: CategoryBetTypeSettingCriteria) : string => {
	let key = sportType.toString();

	if (sportType == 50 && gameType != 'none') {
		key = gameType == 'fixed' ? `${key}-FX` : `${key}-FC`
	}

	if (sportType == 6 || sportType == 8 ||
		(sportType == 50 && gameType == 'fixed')) {
		key = isLive ? `${key}_l` : `${key}_d`
	}

	return key;
}

export const getCustomizeLayoutType = (betType: number, view: ViewEnum = ViewEnum.galaxy, sportId: number = 0): LayoutType | undefined => {
	return viewLayoutSettings[view]?.[betType]?.[sportId] 
		?? viewLayoutSettings[view]?.[betType]?.[0];
}

export const getBetTypeTutorialSetting = (betType: number) => betTypeTutorialSettings[betType];

export const getSpecialSportSetting = (sport: number) => specialSportSettings[sport]

/** Main tab bet types also shown on BB+ corner booking child matches */
const BB_PLUS_CORNER_BOOKING_BET_TYPES = [2, 12, 24] as const;

// BB plus 不顯示的 category
export const isBBPlusHiddenCategory = (category: Category) => {
	return [
		'SingleMatchParlay',
		'Specials',
	].includes(category);
}

export const getNonNormalizedSports = () => new Set([...numberGameIds, ...virtualSportIds]);

// for vs2 tournament sort
export const sortVirtualSports = <T extends { GameId: string | number }>(items: T[], virtualSoccerList?: number[]): T[] => {
	// virtual sports sort 後續如果要改動vs 排序 改這裡的sortid
	// '180' is conditionally included based on nonSport?.virtualSoccerList length > 0
	const baseOrder = ['112', '116', '134', '190', '193', '192', '194', '195', '197', '191', '196', '198', '177'];
	const virtualSoccerOrder = sportService.hasVirtualSoccerList(virtualSoccerList) ? ['180'] : [];
	const restOrder = ['179', '147', '125', '126', '127', '188', '186'];
	const virtualSportsOrder = [...baseOrder, ...virtualSoccerOrder, ...restOrder];
	const getGameId = (gameId: string | number): string => {
		if (typeof gameId === 'string') {
			return gameId.match(/\d+/)?.[0] || '';
		}
		return gameId.toString();
	};

	return [...items].sort((a, b) => {
		const aId = getGameId(a.GameId);
		const bId = getGameId(b.GameId);

		const aIndex = virtualSportsOrder.indexOf(aId);
		const bIndex = virtualSportsOrder.indexOf(bId);

		if (aIndex === -1 && bIndex === -1) return 0;
		if (aIndex === -1) return 1;
		if (bIndex === -1) return -1;
		return aIndex - bIndex;
	});
};